import React from "react";

import {
  Checkbox as MuiCheckbox,
  CheckboxProps,
  FormControlLabel,
  FormControlLabelProps,
} from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import InfoIcon from "Components/Generic/Icons/Info";

import { Placement } from "../../../util/enums";
import { ToolTipInfo, TooltipPlacements } from "../ToolTipInfo";
import useStyles, { CheckboxLabel, ContainerTooltipWithLabel, IconInfoStyle } from "./styles";

interface Control extends CheckboxProps {
  "data-cy"?: string;
  disabled?: boolean;
  error?: boolean;
}

export interface Props extends Omit<FormControlLabelProps, "control" | "label"> {
  control?: Control;
  checked?: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<any>;
  label?: string;
  labelPlacement?: Placement;
  name?: string;
  onChange?: (event: React.ChangeEvent<any>, checked: boolean) => void;
  value?: unknown;
  error?: boolean;
  labelDisabled?: boolean;
  tooltipText?: string;
  tooltipPlacement?: TooltipPlacements;
  disabledTooltipText?: string;
}

const Checkbox = (props: Props) => {
  const { checked, disabled, error, label, onChange } = props;
  const { tooltipText, disabledTooltipText, tooltipPlacement, ...subProps } = props;

  const classes = useStyles({ error, disabled });

  const renderCheckBoxComponent = () => {
    return (
      <MuiCheckbox
        color="primary"
        checked={checked}
        className={classes.root}
        checkedIcon={<span className={classes.checkedIcon} />}
        icon={<span className={classes.icon} />}
        disableRipple
        error={error}
        inputProps={{ "aria-label": `${label}-checkbox` }}
        onChange={onChange}
        disabled={disabled}
        {...props.control}
      />
    );
  };

  const renderTooltip = () => {
    if (!tooltipText && !disabledTooltipText) return;

    return (
      <ToolTipInfo title={disabledTooltipText || tooltipText || ""} placement={tooltipPlacement}>
        <IconInfoStyle>
          <InfoIcon color={colors.gray600} width={22} height={22} />
        </IconInfoStyle>
      </ToolTipInfo>
    );
  };

  const renderControlLabel = () => {
    return (
      <FormControlLabel
        {...subProps}
        label={
          <CheckboxLabel className={props?.className ? props?.className : ""} disabled={disabled}>
            <ContainerTooltipWithLabel>
              {label}
              {renderTooltip()}
            </ContainerTooltipWithLabel>
          </CheckboxLabel>
        }
        control={renderCheckBoxComponent()}
        className={classes.controlLabel}
      />
    );
  };

  if (label) return renderControlLabel();

  return renderCheckBoxComponent();
};

export default Checkbox;
